import React, {useState, useEffect} from 'react';
import {
    Grid,
    GridItem,
    Flex,
    Text,
    Box,
    Stack,
    IconButton,
    HStack,
    Spinner,
    Button,
    Icon,
    Tag,
} from '@chakra-ui/react';
import {get} from 'lodash';
// components
import TreeView from 'components/TreeView';
import CardContainer from 'components/CardContainer';
import Page from 'components/Page';
import CustomTable from 'components/CustomTable';
import DateRangeSelector from 'components/DateRangeSelector';
import {ExportButton} from 'components/StyledBtn';
import ClientIbAgentClosedOrderTable from 'components/Tables/ClientIbAgentClosedOrderTable';
import EditMT4RebateDialog from 'components/Dialog/EditMT4RebateDialog';
import EditMT5RebateDialog from 'components/Dialog/EditMT5RebateDialog';
import EditUpRebateDialog from 'components/Dialog/EditUpRebateDialog';
import IBClosedOrderDialog from 'components/Dialog/IBClosedOrderDialog';
import HelpTip from 'components/HelpTip';
// hooks
import {useTranslation} from 'react-i18next';
import {useUserIbDetail, useTreeView} from 'hooks/datahook/useIbAgent';
import {getDirectChildren} from 'data/treeview';
import {useUserForexAccountList, useCloseOrders} from 'hooks/datahook/forex';
import {useAuthStore} from 'stores/authStore';
import {useUserClosedOrderSummary} from 'hooks/datahook/useTransaction';
import { useUIStore } from 'stores/uiStore';
// icons
import {AiOutlineRollback} from 'react-icons/ai';
import {HiOutlineEye} from 'react-icons/hi';
import {BiEditAlt} from 'react-icons/bi';
import TableMenu from 'components/TableCell/TableMenu';
import {percentageFormator, formatDayMonthYear} from '../../../utils/formator';
import numeral from 'numeral';
import { StyledButton } from 'components/StyledBtn';
import { useAuth } from 'hooks/useAuth';
import {useProfile} from 'hooks/datahook/auth';
import MostActiveTraders from './MostActiveTraders';

/**
 * Clients & sub IBs page.
 *
 * Props (all optional, the client portal passes none of them):
 *   - `id`     the IB whose sub-levels are listed (the logged-in user is
 *              the fallback)
 *   - `user`   the IB's user record — the tree is looked up by its id
 *              (the logged-in user's own record is the fallback)
 *   - `newUi`  whether the "Most active traders" panel is rendered (the
 *              client's own preference is the fallback)
 *   - `manageHeader`  whether the page writes its title into the portal
 *              header (the client portal does; the admin console passes
 *              false so it does not overwrite the admin portal's header)
 */
const Sublevels = ({
    id: idProp,
    user: userProp,
    newUi: newUiProp,
    manageHeader = true,
}) => {
    const [loginState, setLoginState] = useState(null);
    const [mt4Open, setMt4Open] = useState(false);
    const [mt5Open, setMt5Open] = useState(false);
    const [mt4UpOpen, setMt4UpOpen] = useState(false);
    const [mt5UpOpen, setMt5UpOpen] = useState(false);
    const [closedOrderOpen, setClosedOrderOpen] = useState(false);
    const [date, setDate] = useState({
        start: null,
        end: null,
    });
    const [subIbDetail, setSubIbDetail] = useState(null);
    const {user: authUser} = useAuthStore();

    const [setHeaderTitle, clearHeaderTitle] = useUIStore((state) => [
        state.setHeaderTitle,
        state.clearHeaderTitle,
    ]);

    useEffect(()=>{
        // admin 控制台渲染这一页时不碰门户顶栏 (既不 set 也不 clear).
        if (!manageHeader) return;
        setHeaderTitle(t('referredClients'))
        return () => {
            clearHeaderTitle()
        }
    }, [])
    const {id: authId} = useAuth()
    // The admin console renders this page for the client it is looking at and
    // passes that client's id / user record in; on the client side (no prop)
    // the logged-in user stays the target.
    const id = idProp ?? authId;
    const user = userProp ?? authUser;
    // 2026-10-20 M2: 与 IBLayout 同一来源判断 newUi; 仅新 UI 在 margin 容器内
    // 渲染 Most active traders 面板, 旧 UI 完全不受影响
    const {data: profile} = useProfile();
    const newUi = newUiProp ?? Boolean(get(profile, 'preference.newUi'));

    const {
        data: accounts,
        page,
        setPage,
        totalPage,
        totalItems,
        pageSize,
        isLoading: accountLoading
    } = useUserForexAccountList({
        user: get(subIbDetail, 'userId'),
    });

    const swr = useUserClosedOrderSummary({
        accountId: loginState?.id,
        startDate: date?.start,
        endDate: date?.end,
        pageSize: 20,
        // onlyRewired: loginState?.rawData?.rewireTo,
        onlyRewired: false,
        rewireToCurrent: loginState?.rawData?.rewireTo+'' === id+'',
        rewireDate: loginState?.rewireUpdateAt
    });
    const {data, isLoading} = useTreeView(user);
    const {MT4Rebate, MT5Rebate,mutate} = useUserForexAccountList({
        user: get(data, '[0].userId'),
        type: 'Rebate',
    });
    // 选中节点自己的 Rebate 账号（决定 MT4/MT5 Rebate account 行是否显示及显示值）
    const {
        MT4Rebate: nodeMT4Rebate,
        MT5Rebate: nodeMT5Rebate,
        isMT4RebateRecommended: isNodeMT4RebateRecommended,
        isMT5RebateRecommended: isNodeMT5RebateRecommended,
    } = useUserForexAccountList({
        user: get(subIbDetail, 'userId'),
        type: 'Rebate',
    });
    const {data: detail, mutate: mutateDetail} = useUserIbDetail({
        nodeId: get(subIbDetail, 'id'),
    });
    // CRM-758：只有直接下级节点才能设置"上传给当前用户的佣金"的路由账户
    // (树里多了一层 campaign 分组，直接下级要展平那一层)
    const isDirectChild = getDirectChildren(data).some(
        (item) => get(item, 'id') === get(subIbDetail, 'id'),
    );
    const isSelfNode = get(detail, 'ref') === get(data, '[0].ref');
    // 上级是 Admin admin（user id = 100，占位用户无 Rebate）或没有上级时，不显示 Upper level 行
    // agentUserId 缺失（后端未返回该字段）时保持显示，兼容旧接口
    const agentUserId = get(detail, 'agentUserId');
    const showUpRebate = agentUserId === undefined ? true : Boolean(agentUserId) && Number(agentUserId) !== 100;
    // 上级路由账户的编辑入口：直接下级，或自己节点且上级非 Admin admin（user id = 100）时可编辑
    const canEditUpRebate = isDirectChild || (isSelfNode && Boolean(agentUserId) && Number(agentUserId) !== 100);

    const {t} = useTranslation('app');
    const menu = (rowData) => [
        {
            label: t('view'),
            value: 'view',
            onClick: (rowData) => {
                setLoginState(rowData);
            },
            icon: HiOutlineEye,
        },
    ];
    // const {data: subIbDetail} = useSubIbDetail(user)
    const column = [
        {
            label: t('accountType'),
            dataKey: 'accountType',
            columProps: {
                flexGrow: 1,
            },
            renderCell: (rowData) => {
                return `${get(rowData, 'accountSource')} ${get(
                    rowData,
                    'type',
                )}`;
            },
        },
        {
            label: t('accountNum'),
            dataKey: 'login',
            columProps: {
                flexGrow: 1,
            },
        },
        {
            label: t('currency'),
            dataKey: 'currency',
            columProps: {
                flexGrow: 1,
            },
        },
        // {
        //     label: t('status'),
        //     dataKey: 'status',
        //     columProps: {
        //         flexGrow: 1,
        //     },
            // renderCell: (rowData) => {
            //     return get(rowData, 'clientDetail.saleDetail.saleCode') &&
            //         get(rowData, 'clientDetail.ibCode')
            //         ? `${get(
            //               rowData,
            //               'clientDetail.saleDetail.saleCode',
            //           )}.${get(rowData, 'clientDetail.ibCode')}`
            //         : get(rowData, 'clientDetail.saleDetail.saleCode') &&
            //           !get(rowData, 'clientDetail.ibCode')
            //         ? get(rowData, 'clientDetail.saleDetail.saleCode')
            //         : !get(rowData, 'clientDetail.saleDetail.saleCode') &&
            //           get(rowData, 'clientDetail.ibCode')
            //         ? get(rowData, 'clientDetail.ibCode')
            //         : '';
            // },
        // },
        {
            label: t('closedOrders'),
            dataKey: 'closeOrders',
            columProps: {
                flexGrow: 1,
                align: 'right'
            },
            renderCell: (rowData) => {
                if (get(rowData, 'type') === 'Rebate') {
                    return '';
                }
                return numeral(get(rowData, 'rawData.volume')).format('0,0.00') ;
            },
        },
        {
            label: t(''),
            dataKey: 'action',
            columProps: {
                align: 'center',
                flexGrow: 1,
            },
            renderCell: (rowData) => {
                if (get(rowData, 'type') === 'Rebate') {
                    return '';
                }
                return (
                    <StyledButton
                        onClick={() => onRowClick(rowData)}
                        variant="primary"
                        fontWeight={400}
                        fontSize={10}
                        size="xs"
                        h={'20px'}
                    >
                        {t('viewMore')}
                    </StyledButton>
                );
            },
        },
    ];

    const onRowClick = (value) => {
        setLoginState(value);
        setClosedOrderOpen(true);
    };
    return (
        <Page
            title={t('sublevels')}
            style={{height: 'calc(100vh - 135px)', overflow: 'hidden'}}
        >
            {/* 2026-10-20 任务 L: 对齐新 UI 卡片页 .nx .main 的页面边距
                (newui.css:99 width:1280px; margin:0 auto; padding:28px 32px 60px),
                但刻意不加 .nx 类 —— .nx *{margin:0;padding:0} 会清零旧 Chakra 页内部间距。
                height:100% 让内部 HStack 的 h="100%" 落在 padding 后的内容盒里。
                用原生 div + 内联 style(不用 Chakra Box): 边距是布局语义, 不进主题系统,
                也避免 emotion 类名化后无法直读。 */}
            <div
                style={{
                    width: '100%',
                    maxWidth: '1280px',
                    margin: '0 auto',
                    padding: '28px 32px 60px',
                    boxSizing: 'border-box',
                    height: '100%',
                }}
            >
            {/* 2026-10-20 M2: Most active traders 面板, 只在 newUi=true 时渲染,
                位于 HStack 之前(页面上方) */}
            {newUi && <MostActiveTraders id={id} />}
            <HStack spacing={4} h="100%" alignItems={'flex-start'}>
                <Stack
                    width={350}
                    display="flex"
                    flexDirection={'column'}
                    h="100%"
                >
                    <CardContainer h="100%">
                        {isLoading ? (
                            <Box
                                display={'flex'}
                                justifyContent="center"
                                alignItems={'center'}
                                h="100%"
                            >
                                <Spinner />
                            </Box>
                        ) : data?.length ? (
                            <Box overflow={'auto'}>
                                <TreeView
                                    data={data}
                                    setSubIbDetail={setSubIbDetail}
                                    isClient={true}
                                />
                            </Box>
                        ) : (
                            <Box
                                display={'flex'}
                                alignItems={'center'}
                                justifyContent="center"
                            >
                                {t('noData')}
                            </Box>
                        )}
                    </CardContainer>
                </Stack>
                <Stack flex={1} h="100%">
                    <CardContainer h="100%">
                        {data?.length ? (
                            <>
                                <Box>
                                    <Stack direction={{base: 'column',lg: 'row'}}>
                                        <Box flex={1}>
                                            <Text mb={2} fontWeight={500}>
                                                {get(subIbDetail, 'childrenName')}
                                            </Text>
                                        </Box>
                                        <Flex fontSize={'sm'} flex={1}>
                                            <Text mr={1}>
                                                {t('registerDate')}:
                                            </Text>
                                            <Text>
                                                {formatDayMonthYear(get(detail, 'createdAt'))}
                                            </Text>
                                        </Flex>
                                    </Stack>
                                    <Stack>
                                        {/* <Stack
                                            direction={{
                                                base: 'column',
                                                lg: 'row',
                                            }}
                                        >
                                            <Flex fontSize={'sm'} flex={1}>
                                                <Text mr={1}>
                                                    {get(detail, 'type') ===
                                                    'ib'
                                                        ? t('ibRef')
                                                        : t('clientRef')}
                                                    :
                                                </Text>
                                                <Text>
                                                    {get(detail, 'ref')}
                                                </Text>
                                            </Flex>
                                            {get(detail, 'type') === 'ib' ? (
                                                <Flex fontSize={'sm'} flex={1}>
                                                    <Text mr={1}>
                                                        {t('ibCode')}:
                                                    </Text>
                                                    <Text>
                                                        {get(detail, 'ibCode')}
                                                    </Text>
                                                </Flex>
                                            ) : (
                                                ''
                                            )}
                                        </Stack> */}
                                        <Stack direction={{
                                                base: 'column',
                                                lg: 'row',
                                            }}>
                                            {get(detail, 'type') ===
                                            'client' ? (
                                                ''
                                            ) : (
                                                <Flex fontSize={'sm'} flex={1}>
                                                    <Text mr={1}>
                                                        {t('ibRate')}:
                                                    </Text>
                                                    <Text>
                                                        {get(
                                                            detail,
                                                            'commissionRate',
                                                        )
                                                            ? percentageFormator(
                                                                  get(
                                                                      detail,
                                                                      'commissionRate',
                                                                  ),
                                                              )
                                                            : ''}
                                                    </Text>
                                                </Flex>
                                            )}
                                            {/* <Flex fontSize={'sm'} flex={1}>
                                                    <Text mr={1}>
                                                        {t('sale')}:
                                                    </Text>
                                                    <Text>
                                                        {get(detail, 'saleName')}
                                                    </Text>
                                                </Flex> */}
                                        </Stack>
                                        <Stack
                                            direction={{
                                                base: 'column',
                                                lg: 'row',
                                            }}
                                        >
                                            {nodeMT4Rebate ? (
                                                <Flex
                                                    fontSize={'sm'}
                                                    flex={1}
                                                >
                                                    <Text mr={1}>
                                                        {t(
                                                            'mt4RebateAccount',
                                                        )}
                                                        :
                                                    </Text>
                                                    <HelpTip
                                                        text={t(
                                                            'rebateAccountTip',
                                                        )}
                                                    />
                                                    <Text
                                                        display={'flex'}
                                                        alignItems="center"
                                                    >
                                                        {get(
                                                            nodeMT4Rebate,
                                                            'login',
                                                        )}
                                                        {isNodeMT4RebateRecommended ? (
                                                            <>
                                                                <Tag
                                                                    size={'sm'}
                                                                    variant="subtle"
                                                                    colorScheme="orange"
                                                                    ml={2}
                                                                >
                                                                    {t('systemRecommended')}
                                                                </Tag>
                                                                <HelpTip
                                                                    text={t('recommendedRebateAccountTip')}
                                                                />
                                                            </>
                                                        ) : null}
                                                        {isSelfNode ? (
                                                            <Box ml={2}>
                                                                <BiEditAlt
                                                                    onClick={() =>
                                                                        setMt4Open(
                                                                            true,
                                                                        )
                                                                    }
                                                                />
                                                            </Box>
                                                        ) : null}
                                                    </Text>
                                                </Flex>
                                            ) : null}
                                            {nodeMT5Rebate ? (
                                                <Flex
                                                    fontSize={'sm'}
                                                    flex={1}
                                                >
                                                    <Text mr={1}>
                                                        {t(
                                                            'mt5RebateAccount',
                                                        )}
                                                        :
                                                    </Text>
                                                    <HelpTip
                                                        text={t(
                                                            'rebateAccountTip',
                                                        )}
                                                    />
                                                    <Text
                                                        display={'flex'}
                                                        alignItems="center"
                                                    >
                                                        {get(
                                                            nodeMT5Rebate,
                                                            'login',
                                                        )}
                                                        {isNodeMT5RebateRecommended ? (
                                                            <>
                                                                <Tag
                                                                    size={'sm'}
                                                                    variant="subtle"
                                                                    colorScheme="orange"
                                                                    ml={2}
                                                                >
                                                                    {t('systemRecommended')}
                                                                </Tag>
                                                                <HelpTip
                                                                    text={t('recommendedRebateAccountTip')}
                                                                />
                                                            </>
                                                        ) : null}
                                                        {isSelfNode ? (
                                                            <Box ml={2}>
                                                                <BiEditAlt
                                                                    onClick={() =>
                                                                        setMt5Open(
                                                                            true,
                                                                        )
                                                                    }
                                                                />
                                                            </Box>
                                                        ) : null}
                                                    </Text>
                                                </Flex>
                                            ) : null}
                                        </Stack>
                                        {showUpRebate ? (
                                            <Stack
                                                direction={{
                                                    base: 'column',
                                                    lg: 'row',
                                                }}
                                            >
                                                <Flex
                                                    fontSize={'sm'}
                                                    flex={1}
                                                >
                                                    <Text mr={1}>
                                                        {t(
                                                            'mt4UpRebateAccount',
                                                        )}
                                                        :
                                                    </Text>
                                                    <HelpTip
                                                        text={t(
                                                            'upRebateAccountTip',
                                                        )}
                                                    />
                                                    <Text
                                                        display={'flex'}
                                                        alignItems="center"
                                                    >
                                                        {get(
                                                            detail,
                                                            'upRebateAccountMT4Login',
                                                        ) ||
                                                            (isDirectChild &&
                                                            MT4Rebate
                                                                ? get(
                                                                      MT4Rebate,
                                                                      'login',
                                                                  )
                                                                : '')}
                                                        {canEditUpRebate ? (
                                                            <Box ml={2}>
                                                                <BiEditAlt
                                                                    onClick={() =>
                                                                        setMt4UpOpen(
                                                                            true,
                                                                        )
                                                                    }
                                                                />
                                                            </Box>
                                                        ) : null}
                                                    </Text>
                                                </Flex>
                                                <Flex
                                                    fontSize={'sm'}
                                                    flex={1}
                                                >
                                                    <Text mr={1}>
                                                        {t(
                                                            'mt5UpRebateAccount',
                                                        )}
                                                        :
                                                    </Text>
                                                    <HelpTip
                                                        text={t(
                                                            'upRebateAccountTip',
                                                        )}
                                                    />
                                                    <Text
                                                        display={'flex'}
                                                        alignItems="center"
                                                    >
                                                        {get(
                                                            detail,
                                                            'upRebateAccountMT5Login',
                                                        ) ||
                                                            (isDirectChild &&
                                                            MT5Rebate
                                                                ? get(
                                                                      MT5Rebate,
                                                                      'login',
                                                                  )
                                                                : '')}
                                                        {canEditUpRebate ? (
                                                            <Box ml={2}>
                                                                <BiEditAlt
                                                                    onClick={() =>
                                                                        setMt5UpOpen(
                                                                            true,
                                                                        )
                                                                    }
                                                                />
                                                            </Box>
                                                        ) : null}
                                                    </Text>
                                                </Flex>
                                            </Stack>
                                        ) : (
                                            ''
                                        )}
                                    </Stack>
                                </Box>
                                <Text fontSize={'lg'} pt={4} fontWeight={500}>
                                    {t('tradingAccounts')}
                                </Text>
                                <Stack overflow={'auto'}>
                                    <CustomTable
                                        data={accounts}
                                        page={page}
                                        setPage={setPage}
                                        totalPage={totalPage}
                                        totalItems={totalItems}
                                        column={column}
                                        pageSize={pageSize}
                                        // onRowClick={onRowClick}
                                        title={t('fxTradeAccounts')}
                                        isLoading={accountLoading}
                                        noCard
                                    />
                                </Stack>
                            </>
                        ) : (
                            <Box
                                display={'flex'}
                                alignItems="center"
                                justifyContent={'center'}
                                h="100%"
                            >
                                {t('noData')}
                            </Box>
                        )}
                    </CardContainer>
                </Stack>
            </HStack>
            </div>
            <EditMT4RebateDialog
                mutate={mutate}
                value={get(MT4Rebate, 'id',)}
                nodeId={get(subIbDetail, 'id')}
                user={get(data, '[0].userId')}
                open={mt4Open}
                onClose={() => setMt4Open(false)}
            />
            <EditMT5RebateDialog
                mutate={mutate}
                value={get(MT5Rebate, 'id',)}
                nodeId={get(subIbDetail, 'id')}
                user={get(data, '[0].userId')}
                open={mt5Open}
                onClose={() => setMt5Open(false)}
            />
            <EditUpRebateDialog
                source="MT4"
                mutate={mutateDetail}
                value={get(detail, 'upRebateAccountMT4') || (isDirectChild ? get(MT4Rebate, 'id') : null)}
                nodeId={get(subIbDetail, 'id')}
                user={isDirectChild ? get(data, '[0].userId') : get(detail, 'agentUserId')}
                open={mt4UpOpen}
                onClose={() => setMt4UpOpen(false)}
            />
            <EditUpRebateDialog
                source="MT5"
                mutate={mutateDetail}
                value={get(detail, 'upRebateAccountMT5') || (isDirectChild ? get(MT5Rebate, 'id') : null)}
                nodeId={get(subIbDetail, 'id')}
                user={isDirectChild ? get(data, '[0].userId') : get(detail, 'agentUserId')}
                open={mt5UpOpen}
                onClose={() => setMt5UpOpen(false)}
            />
            <IBClosedOrderDialog
                open={closedOrderOpen}
                onClose={() => setClosedOrderOpen(false)}
                dailySwr={swr}
                loginState={loginState}
                setLoginState={setLoginState}
                date={date}
                setDate={setDate}
                onlyRewired={loginState?.rawData?.rewireTo}
                rewireToCurrent={loginState?.rawData?.rewireTo+'' === id+''}
                rewireDate={loginState?.rewireUpdateAt}
            />
        </Page>
    );
};

export default Sublevels;
